import React from 'react';
import { Check } from 'lucide-react';

export const HowWeOperateSection = () => {
  const principles = [
    {
      number: 1,
      title: 'Community-Funded Independence',
      description: 'We don\'t rely on grants to survive. Our $14.95–$49.99 CAD/month memberships fund operations, keeping us agile and mission-driven. We can move fast, stay ethical, and never compromise values for funding.'
    },
    {
      number: 2,
      title: 'No Warehouses, No Waiting',
      description: 'Same-day, hyper-local redistribution. City squads partner with local businesses to collect surplus essentials and deliver them the same day. No stockpiling. No bureaucracy. Just dignity and speed.'
    },
    {
      number: 3,
      title: 'Decentralized Storytelling (60% Grassroots)',
      description: 'Women on the ground create 60% of our content using mobile toolkits. Narratives stay authentic, consent-first, and rooted in lived experience — not performance.'
    },
    {
      number: 4,
      title: 'Earned Opportunities, Not Popularity Contests',
      description: 'Access to our incubator, pitch events, and grants is based on readiness and commitment — not follower counts, connections, or who you know.'
    },
    {
      number: 5,
      title: 'Transparent MEL (Monitoring, Evaluation, Learning)',
      description: 'Every dollar tracked. Every impact measured. Every city reports monthly. Dashboards aligned with SDG 5 and UN Women frameworks. Year-end surplus automatically reallocated to shelters and incubator grants.'
    },
    {
      number: 6,
      title: 'Governed Like an Institution, Led Like a Movement',
      description: 'Formal bylaws, board oversight, treasurer accountability, CRA/Revenu Québec compliance, and audit-ready ledgers. We move with the speed and heart of grassroots organizers.'
    },
    {
      number: 7,
      title: 'Volunteers Are Valued (And Compensated)',
      description: 'Field volunteers receive $60/day stipends for street campaign activations (up to 2 days/month). Speakers are compensated. We don\'t exploit labor in the name of "volunteerism."'
    },
    {
      number: 8,
      title: 'No Savior Narratives, Ever',
      description: 'We don\'t "help" women. We redistribute power, resources, and access. Consent-first. Dignity-centered. Co-created. No victim narratives. No poverty porn.'
    }
  ];

  const resultAttributes = [
    'Fast — same-day redistribution, monthly activations',
    'Scalable — 25+ countries without losing soul',
    'Ethical — consent-first, transparent, dignity-centered',
    'Sustainable — community-funded, not grant-dependent',
    'Institutional-grade — MEL systems, safeguarding, governance',
    'Grassroots-powered — led by women, for women, with women'
  ];

  return (
    <section id="how-we-operate" className="py-20" style={{ backgroundColor: 'var(--white-soft)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="lhsc-heading text-3xl md:text-5xl mb-6">
            A MODEL BUILT DIFFERENT.
          </h2>
          <p className="lhsc-body max-w-3xl mx-auto">
            Most nonprofits choose one lane: provide services OR advocate OR redistribute. 
            We built a circular system that does all three — simultaneously.
          </p>
        </div>

        {/* Eight Operating Principle Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {principles.map((principle) => (
            <div
              key={principle.number}
              className="bg-white p-6 border border-[var(--pink-dusty)]"
            >
              <div className="flex items-start">
                <div 
                  className="text-3xl lhsc-heading mr-4 flex-shrink-0"
                  style={{ color: 'var(--mauve-rose)' }}
                >
                  {principle.number}
                </div>
                <div>
                  <h3 className="lhsc-heading text-base mb-3">{principle.title}</h3>
                  <p className="lhsc-body text-sm">{principle.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* The Result Grid */}
        <div className="mb-12">
          <h3 className="lhsc-heading text-2xl text-center mb-8">The Result</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {resultAttributes.map((attribute, index) => (
              <div
                key={index}
                className="flex items-start p-4 bg-white border border-[var(--pink-dusty)]"
              >
                <Check size={20} className="mr-3 flex-shrink-0 mt-1" style={{ color: 'var(--mauve-rose)' }} />
                <span className="lhsc-body text-sm">{attribute}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pull Quote */}
        <div className="max-w-4xl mx-auto text-center">
          <blockquote className="lhsc-pull-quote text-2xl md:text-3xl">
            "This is what external analysis called 'a redistributive system dressed as a movement.' 
            We agree. And we're just getting started."
          </blockquote>
        </div>
      </div>
    </section>
  );
};
